import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { CreateShowRequest, ShowType, ShowTipTotals, TipSummary, JukeboxNowPlayingSummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth, StreamHealthBanner, useLivePublisher, useLiveSubscriber, useSpeechCaptions } from '@fetmode/app-core';
import { t, getLocale } from '@fetmode/i18n';
import { formatCoins, formatUsername, isPublicMainStage } from '@fetmode/utils';
import { Button } from '@fetmode/ui';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { StudioGlobalChat } from '../../shared/components/StudioGlobalChat/index.js';
import { useStudioModel } from '../studio/index.js';
import { BroadcasterPreview, type BroadcasterPreviewStats } from './BroadcasterPreview.js';
import { StreamQualityMeter } from './StreamQualityMeter.js';
import { StreamSessionTools, useStreamNavigationLock } from './StreamSessionTools.js';
import { ShowTypeStartPanel } from './ShowTypeStartPanel.js';
import { ShowLiveManagementPanel } from './ShowLiveManagementPanel.js';
import { RoomAudiencePanel } from './RoomAudiencePanel.js';
import { ShowLiveStatsBar } from './ShowLiveStatsBar.js';
import { BillingHoldBanner } from './BillingHoldBanner.js';
import { RoxyBroadcastHost, shouldSkipCaptionForRoxy } from './RoxyBroadcastHost.js';
import { useColumnResize } from './useColumnResize.js';
import { useStreamInactivity } from './useStreamInactivity.js';
import './LiveControlsPage.css';

const TIP_POLL_MS = 5_000;
const JUKEBOX_POLL_MS = 2_000;

const IDLE_STATS: BroadcasterPreviewStats = {
  width: 0,
  height: 0,
  fps: 0,
  hasAudio: false,
  hasVideo: false,
};

export function LiveControlsPage(): ReactElement {
  const { api } = useAuth();
  const navigate = useNavigate();
  const {
    profile,
    liveShow,
    isLoading,
    setLiveShow,
    refresh,
    requestsRevision,
    sendStreamSignal,
    sendShowCaption,
    streamPeerId,
    subscribeStreamSignals,
  } = useStudioModel();
  const [error, setError] = useState<string | null>(null);
  const [focusPrivateFan, setFocusPrivateFan] = useState<{ userId: string; username: string } | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentTips, setRecentTips] = useState<TipSummary[]>([]);
  const [showTipTotals, setShowTipTotals] = useState<ShowTipTotals | null>(null);
  const [nowPlaying, setNowPlaying] = useState<JukeboxNowPlayingSummary | null>(null);
  const [nowPlayingProgress, setNowPlayingProgress] = useState(0);
  const [previewStats, setPreviewStats] = useState<BroadcasterPreviewStats>(IDLE_STATS);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const {
    layoutRef,
    columnWidthPx: stageWidthPx,
    isDragging: isStageDragging,
    onHandlePointerDown: onStageHandlePointerDown,
  } = useColumnResize(
    liveShow
      ? {
          layoutKey: 'streaming',
          minRightPx: 450,
        }
      : 'setup',
  );
  const {
    layoutRef: chatSplitRef,
    columnWidthPx: audienceWidthPx,
    isDragging: isChatDragging,
    onHandlePointerDown: onChatHandlePointerDown,
  } = useColumnResize({
    layoutKey: liveShow ? 'chat-audience' : 'idle',
    storageKey: 'fm.studio.stream.audienceColWidthPx',
    anchor: 'end',
    defaultRatio: 0.2,
    defaultWidthPx: 220,
    minLeftPx: 240,
    minRightPx: 180,
    maxStoredPx: 480,
  });

  const isOnBreak = liveShow?.status === 'PAUSED';
  const isAway = liveShow?.status === 'AWAY';
  const isSoftHold = isOnBreak || isAway;
  const isHardLive =
    liveShow !== null && (liveShow.status === 'LIVE' || liveShow.status === 'WAITING');
  useStreamNavigationLock(isHardLive);

  const handlePreviewStats = useCallback((stats: BroadcasterPreviewStats) => {
    setPreviewStats(stats);
  }, []);

  const handlePreviewStream = useCallback((stream: MediaStream | null) => {
    setCameraStream(stream);
  }, []);

  const publishEnabled =
    cameraStream !== null &&
    liveShow !== null &&
    (liveShow.status === 'LIVE' || liveShow.status === 'PAUSED' || liveShow.status === 'AWAY');

  const handleMarkAway = useCallback((): void => {
    if (!liveShow || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    void api
      .markAway(liveShow.id)
      .then((result) => {
        setLiveShow(result.show);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not mark Away.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [api, isSubmitting, liveShow, setLiveShow]);

  const handleAutoEndFromAway = useCallback((): void => {
    if (!liveShow || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    void api
      .endShow(liveShow.id)
      .then(() => {
        setLiveShow(null);
        setError(t('common.studio.live.awayEndedBody'));
        void refresh();
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not end show.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [api, isSubmitting, liveShow, refresh, setLiveShow]);

  const { phase: inactivityPhase, dismissPrompt } = useStreamInactivity({
    enabled: liveShow !== null && (liveShow.status === 'LIVE' || liveShow.status === 'AWAY'),
    mediaStream: cameraStream,
    showStatus: liveShow?.status ?? null,
    onMarkAway: handleMarkAway,
    onAutoEnd: handleAutoEndFromAway,
  });

  useSpeechCaptions({
    enabled: publishEnabled,
    lang: getLocale() === 'es' ? 'es-ES' : 'en-US',
    onCaption: (text, isFinal) => {
      if (!liveShow || shouldSkipCaptionForRoxy(text)) {
        return;
      }
      sendShowCaption({ showId: liveShow.id, text, isFinal });
    },
  });

  const { health: publishHealth } = useLivePublisher({
    enabled: publishEnabled,
    showId: liveShow?.id ?? null,
    localPeerId: streamPeerId,
    mediaStream: cameraStream,
    sendSignal: sendStreamSignal,
    subscribe: subscribeStreamSignals,
  });

  useEffect(() => {
    if (!liveShow) {
      return;
    }
    const showId = liveShow.id;
    let cancelled = false;

    const beat = async (): Promise<void> => {
      try {
        await api.broadcasterHeartbeat(showId);
      } catch {
        // Server Away/end is source of truth if heartbeats stop.
      }
    };

    void beat();
    const timer = window.setInterval(() => {
      if (!cancelled) {
        void beat();
      }
    }, 20_000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [api, liveShow]);

  const c2cEnabled =
    liveShow?.type === 'EXCLUSIVE' &&
    (liveShow.status === 'LIVE' || liveShow.status === 'PAUSED' || liveShow.status === 'AWAY');
  const { remoteStream: c2cStream } = useLiveSubscriber({
    enabled: Boolean(c2cEnabled && liveShow),
    showId: liveShow?.id ?? null,
    localPeerId: streamPeerId,
    sendSignal: sendStreamSignal,
    subscribe: subscribeStreamSignals,
    channel: 'c2c',
  });

  const handleViewerCountChange = useCallback((count: number) => {
    setLiveShow((current) => {
      if (!current || current.viewerCount === count) {
        return current;
      }
      return { ...current, viewerCount: count };
    });
  }, [setLiveShow]);

  const liveShowId = liveShow?.id ?? null;
  const liveShowStartedAt = liveShow?.startedAt ?? null;

  const loadTips = useCallback(async () => {
    if (!liveShowId) {
      return;
    }

    const result = await api.listShowTips(liveShowId, 15);
    setRecentTips(result.tips);
    setShowTipTotals(result.totals);
  }, [api, liveShowId]);

  const syncNowPlaying = useCallback(async () => {
    if (!liveShowId) {
      setNowPlaying(null);
      setNowPlayingProgress(0);
      return;
    }

    try {
      const result = await api.getJukeboxNowPlaying(liveShowId);
      const playing = result.nowPlaying;
      if (!playing) {
        setNowPlaying(null);
        setNowPlayingProgress(0);
        return;
      }
      const started = Date.parse(playing.startedAt);
      const elapsedSec = Number.isFinite(started)
        ? Math.max(0, (Date.now() - started) / 1000)
        : playing.progress * playing.durationSec;
      setNowPlayingProgress(Math.min(1, elapsedSec / Math.max(1, playing.durationSec)));
      setNowPlaying(playing);
    } catch {
      // Keep last known track on transient poll errors.
    }
  }, [api, liveShowId]);

  useEffect(() => {
    if (!liveShowId) {
      setRecentTips([]);
      setShowTipTotals(null);
      return;
    }

    // Clear immediately on segment change (startedAt resets on each LIVE transition).
    setRecentTips([]);
    setShowTipTotals(null);
    void loadTips().catch(() => undefined);

    const intervalId = window.setInterval(() => {
      void loadTips().catch(() => undefined);
    }, TIP_POLL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [liveShowId, liveShowStartedAt, loadTips]);

  useEffect(() => {
    if (!liveShowId) {
      setNowPlaying(null);
      setNowPlayingProgress(0);
      return;
    }

    void syncNowPlaying();
    const intervalId = window.setInterval(() => {
      void syncNowPlaying();
    }, JUKEBOX_POLL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [liveShowId, liveShowStartedAt, requestsRevision, syncNowPlaying]);

  useEffect(() => {
    if (!nowPlaying) {
      return;
    }
    const duration = Math.max(1, nowPlaying.durationSec);
    const timer = window.setInterval(() => {
      setNowPlayingProgress((current) => Math.min(1, current + 1 / duration));
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [nowPlaying]);

  async function handleStartShow(
    type: ShowType,
    options?: Omit<CreateShowRequest, 'type'>,
  ): Promise<void> {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await api.createShow({ type, ...options });
      setLiveShow(result.show);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start show.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleEndShow(): Promise<void> {
    if (!liveShow) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await api.endShow(liveShow.id);
      setLiveShow(null);
      setRecentTips([]);
      await refresh();
      void navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not end show.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleReturnToPublic(): Promise<void> {
    if (!liveShow) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await api.returnToPublic(liveShow.id);
      setLiveShow(result.show);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not return to Main Stage.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleTakeBreak(): Promise<void> {
    if (!liveShow) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await api.takeBreak(liveShow.id);
      setLiveShow(result.show);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not take a break.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResumeShow(): Promise<void> {
    if (!liveShow) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await api.resumeShow(liveShow.id);
      setLiveShow(result.show);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not resume Main Stage.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return <p className="live-controls__loading">{t('common.studio.dashboard.loading')}</p>;
  }

  if (!profile) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!profile.canBroadcast && !liveShow) {
    return (
      <section className="live-controls live-controls--setup">
        <div className="fm-card">
          <h1 style={{ marginTop: 0 }}>{t('common.studio.live.title')}</h1>
          <p style={{ color: 'var(--fm-color-text-muted)' }}>
            {t('common.studio.onboarding.broadcastLocked')}
          </p>
          <Link to="/onboarding">
            <Button label={t('common.studio.onboarding.finishApplication')} />
          </Link>
        </div>
      </section>
    );
  }

  const showLeaveOptions =
    liveShow !== null && !isSoftHold && !isPublicMainStage(liveShow.type);

  if (!liveShow) {
    return (
      <section className="live-controls live-controls--setup">
        {error ? <p className="fm-error live-controls__error">{error}</p> : null}
        <RoxyBroadcastHost
          liveShow={null}
          isSubmitting={isSubmitting}
          recentTips={recentTips}
          onStartShow={handleStartShow}
          onEndShow={handleEndShow}
          onReturnToPublic={handleReturnToPublic}
          onShowUpdated={setLiveShow}
          onError={setError}
        />
        <div
          ref={layoutRef}
          className={`live-controls__setup-grid${isStageDragging ? ' live-controls__setup-grid--dragging' : ''}`}
          style={{ ['--fm-stream-col' as string]: `${String(stageWidthPx)}px` }}
        >
          <div className="fm-card live-controls__setup-card">
            <ShowTypeStartPanel isSubmitting={isSubmitting} onStart={handleStartShow} />
          </div>
          <button
            type="button"
            className="live-controls__resize"
            aria-label={t('common.studio.live.resizeColumns')}
            onPointerDown={onStageHandlePointerDown}
          />
          <div className="live-controls__setup-chat">
            <StudioGlobalChat variant="panel" defaultMode="private" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="live-controls live-controls--streaming" aria-label={t('common.studio.nav.stream')}>
      {error ? <p className="fm-error live-controls__error">{error}</p> : null}
      <RoxyBroadcastHost
        liveShow={liveShow}
        isSubmitting={isSubmitting}
        recentTips={recentTips}
        onStartShow={handleStartShow}
        onEndShow={handleEndShow}
        onReturnToPublic={handleReturnToPublic}
        onShowUpdated={setLiveShow}
        onError={setError}
      />
      <DemandRateBanner liveShow={liveShow} onShowUpdated={setLiveShow} />

      <div
        ref={layoutRef}
        className={`live-controls__layout${isStageDragging ? ' live-controls__layout--dragging' : ''}`}
        style={{ ['--fm-stream-col' as string]: `${String(stageWidthPx)}px` }}
      >
        <div className="live-controls__stage">
          <div className="live-controls__preview-shell">
            <BroadcasterPreview
              active
              onStats={handlePreviewStats}
              onStream={handlePreviewStream}
              fanStream={c2cStream}
            />
            {inactivityPhase === 'prompt' ? (
              <div className="live-controls__still-there" role="alertdialog" aria-modal="true">
                <div className="live-controls__still-there-card">
                  <h2 className="live-controls__still-there-title">
                    {t('common.studio.live.stillThereTitle')}
                  </h2>
                  <p className="live-controls__still-there-body">
                    {t('common.studio.live.stillThereBody')}
                  </p>
                  <Button
                    label={t('common.studio.live.stillThereStay')}
                    onClick={() => {
                      dismissPrompt();
                    }}
                  />
                </div>
              </div>
            ) : null}
            {isAway ? (
              <div className="live-controls__away-banner" role="status">
                {t('common.studio.live.markAway')}
              </div>
            ) : null}
          </div>
          <StreamHealthBanner
            health={publishHealth}
            paid={liveShow.type !== 'MAIN_STAGE' || (liveShow.billableViewerCount ?? 0) > 0}
            side="model"
          />

          {nowPlaying ? (
            <div className="live-controls__jukebox" aria-live="polite">
              <p className="live-controls__jukebox-kicker">
                {t('common.studio.live.jukeboxNowPlaying')}
              </p>
              <p className="live-controls__jukebox-title">{nowPlaying.title}</p>
              <p className="live-controls__jukebox-meta">
                {nowPlaying.artist}
                {' · '}
                {t('common.studio.live.jukeboxRequestedBy').replace(
                  '{{name}}',
                  formatUsername(nowPlaying.requestedBy),
                )}
              </p>
              <div className="live-controls__jukebox-progress" aria-hidden="true">
                <div style={{ width: `${String(Math.round(nowPlayingProgress * 100))}%` }} />
              </div>
            </div>
          ) : null}

          <div className="live-controls__controls">
            <div className="live-controls__status-row">
              <span className="live-controls__status">
                {isAway
                  ? t('common.studio.live.awayStatus')
                  : isOnBreak
                    ? t('common.studio.live.onBreak')
                    : `${liveShow.access.typeLabel} · ${t('common.studio.live.liveNow')}`}
              </span>
              <div className="live-controls__actions">
                {isSoftHold ? (
                  <>
                    <Button
                      label={t('common.studio.live.resumeMainStage')}
                      disabled={isSubmitting}
                      onClick={() => {
                        void handleResumeShow();
                      }}
                    />
                    <Button
                      label={t('common.studio.live.endAllShows')}
                      variant="secondary"
                      disabled={isSubmitting}
                      onClick={() => {
                        void handleEndShow();
                      }}
                    />
                  </>
                ) : isPublicMainStage(liveShow.type) ? (
                  <Button
                    label={t('common.studio.live.endShow')}
                    variant="secondary"
                    disabled={isSubmitting}
                    onClick={() => {
                      void handleEndShow();
                    }}
                  />
                ) : null}
              </div>
            </div>

            {showLeaveOptions ? (
              <div className="live-controls__leave">
                <p className="live-controls__leave-label">{t('common.studio.live.leaveShowTitle')}</p>
                <div className="live-controls__leave-actions">
                  <Button
                    label={t('common.studio.live.returnToPublic')}
                    disabled={isSubmitting}
                    onClick={() => {
                      void handleReturnToPublic();
                    }}
                  />
                  <Button
                    label={t('common.studio.live.takeBreak')}
                    variant="secondary"
                    disabled={isSubmitting}
                    onClick={() => {
                      void handleTakeBreak();
                    }}
                  />
                  <Button
                    label={t('common.studio.live.endAllShows')}
                    variant="secondary"
                    disabled={isSubmitting}
                    onClick={() => {
                      void handleEndShow();
                    }}
                  />
                </div>
              </div>
            ) : null}

            {!isSoftHold ? (
              <ShowLiveManagementPanel
                liveShow={liveShow}
                isSubmitting={isSubmitting}
                onShowUpdated={(show) => {
                  setLiveShow(show);
                }}
                onError={setError}
              />
            ) : null}

            <div className="live-controls__tips">
              <h2 className="live-controls__section-title">{t('common.studio.live.tipFeed')}</h2>
              {recentTips.length === 0 ? (
                <p className="live-controls__muted">{t('common.studio.live.noTips')}</p>
              ) : (
                <ul className="live-controls__tip-list">
                  {recentTips.map((tip) => (
                    <li key={tip.id}>
                      {t('common.studio.live.tipFrom')
                        .replace('{{username}}', formatUsername(tip.fromUsername))
                        .replace('{{amount}}', formatCoins(tip.amountCoins))}
                      {tip.message ? ` — ${tip.message}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <StreamSessionTools liveShow={liveShow} />
          </div>

          <StreamQualityMeter
            stats={previewStats}
            viewerCount={liveShow.viewerCount}
            spyCount={liveShow.spyCount ?? 0}
            health={publishHealth}
          />
        </div>

        <button
          type="button"
          className="live-controls__resize"
          aria-label={t('common.studio.live.resizeColumns')}
          onPointerDown={onStageHandlePointerDown}
        />

        <div className="live-controls__side">
          <BillingHoldBanner holds={liveShow.billingHolds ?? []} />
          <ShowLiveStatsBar liveShow={liveShow} totals={showTipTotals} />
          <div
            ref={chatSplitRef}
            className={`live-controls__chat-split${isChatDragging ? ' live-controls__chat-split--dragging' : ''}`}
            style={
              audienceWidthPx > 0
                ? { ['--fm-audience-col' as string]: `${String(audienceWidthPx)}px` }
                : undefined
            }
          >
            <div className="live-controls__chat">
              <StudioGlobalChat
                variant="panel"
                defaultMode="room"
                focusPrivateUserId={focusPrivateFan?.userId ?? null}
                focusPrivateUsername={focusPrivateFan?.username ?? null}
                onPrivateFocused={() => {
                  setFocusPrivateFan(null);
                }}
              />
            </div>
            <button
              type="button"
              className="live-controls__resize"
              aria-label={t('common.studio.live.resizeChatAudience')}
              onPointerDown={onChatHandlePointerDown}
            />
            <div className="live-controls__audience">
              <RoomAudiencePanel
                showId={liveShow.id}
                onViewerCountChange={handleViewerCountChange}
                onMessage={setFocusPrivateFan}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DemandRateBanner({
  liveShow,
  onShowUpdated,
}: {
  liveShow: import('@fetmode/types').ShowSummary;
  onShowUpdated: (show: import('@fetmode/types').ShowSummary) => void;
}): ReactElement | null {
  const { api } = useAuth();
  const [isApplying, setIsApplying] = useState(false);
  const demand = liveShow.demandPricing;
  if (!demand || (liveShow.type !== 'VIP' && liveShow.type !== 'EXCLUSIVE')) {
    return null;
  }
  if (demand.mode === 'OFF') {
    return null;
  }

  const levelKey = demand.level.toLowerCase();
  const copy = t('common.studio.live.demandStatus')
    .replace('{{level}}', t(`common.studio.live.demandLevel.${levelKey}`))
    .replace('{{rate}}', String(demand.suggestedCoinsPerMinute))
    .replace('{{current}}', String(demand.appliedCoinsPerMinute));

  return (
    <div className="live-controls__demand" role="status">
      <p>{copy}</p>
      {demand.pending ? (
        <Button
          label={isApplying ? t('common.studio.live.demandApplying') : t('common.studio.live.demandApply')}
          disabled={isApplying}
          onClick={() => {
            void (async () => {
              setIsApplying(true);
              try {
                const result = await api.applyShowDemandPricing(liveShow.id);
                onShowUpdated(result.show);
              } catch {
                /* live poll will refresh */
              } finally {
                setIsApplying(false);
              }
            })();
          }}
        />
      ) : null}
    </div>
  );
}
